<script lang="ts">
  import type { CaptureSession, Output } from "../state/captureSession";
  import { sanitizeFilename } from "../utils/filename";
  import { shareOrDownload } from "../share/share";
  import { Button } from "$lib/components/ui/button";
  import { Input } from "$lib/components/ui/input";
  import { onMount } from "svelte";
  import { lockBodyScroll } from "../utils/scrollLock";

  interface Props {
    session: CaptureSession;
    output: Output;
    pageCount: number;
    onDone: () => void;
  }
  const { session, output, pageCount, onDone }: Props = $props();

  let suggestedName = $derived(session.suggestedName);
  let nameInput = $state("");
  $effect(() => {
    nameInput = $suggestedName;
  });

  let ext = $derived(output.kind === "pdf" ? "pdf" : "jpg");

  async function share() {
    const safe = sanitizeFilename(nameInput);
    suggestedName.set(safe);
    await shareOrDownload(output.blob, `${safe}.${ext}`);
  }

  function formatBytes(n: number): string {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
  }

  onMount(() => {
    return lockBodyScroll();
  });
</script>

<section class="scanlet-shell fixed inset-0 z-50 flex h-dvh overscroll-none flex-col gap-4 overflow-hidden bg-background p-4 sm:p-6" aria-label="Preview">
  <header class="flex items-end justify-between gap-4">
    <div>
      <p class="font-sans text-[0.65rem] font-bold uppercase tracking-[0.28em] text-accent">Ready</p>
      <h1 class="text-4xl font-black leading-none tracking-[-0.08em]">Preview</h1>
    </div>
    <div class="rounded-full border border-border px-3 py-1 font-sans text-xs text-muted-foreground">
      {output.kind === "pdf" ? "PDF" : "JPG"}
    </div>
  </header>

  {#if output.kind === "pdf"}
    <div class="glass-panel min-h-0 flex-1 overscroll-contain overflow-auto rounded-[1.75rem] p-3" aria-label="PDF page previews">
      {#if output.previewUrls.length === 0}
        <div class="flex h-full items-center justify-center text-sm text-muted-foreground" role="status">
          No page preview available. The PDF is ready to share.
        </div>
      {:else}
      <div class="mx-auto flex max-w-xl flex-col gap-4">
        {#each output.previewUrls as url, index}
          <figure class="paper-card rotate-[-0.35deg] rounded-[1.2rem] p-2 shadow-sm even:rotate-[0.35deg]">
            <img src={url} alt={`Page ${index + 1}`} class="block w-full rounded-xl bg-white object-contain" />
            <figcaption class="pt-2 text-center font-sans text-xs font-bold uppercase tracking-[0.2em] text-foreground/55">
              Page {index + 1}
            </figcaption>
          </figure>
        {/each}
      </div>
      {/if}
    </div>
  {:else}
    <img
      src={output.url}
      alt="Preview"
      class="glass-panel min-h-0 flex-1 w-full rounded-[1.75rem] object-contain p-2"
    />
  {/if}

  <div class="glass-panel flex flex-col gap-3 rounded-[1.5rem] p-4">
    <label class="flex flex-col gap-1">
      <span class="font-sans text-xs font-bold uppercase tracking-[0.22em] text-muted-foreground">Name</span>
      <Input bind:value={nameInput} aria-describedby="name-hint" autocomplete="off" class="h-12 rounded-2xl border-white/15 bg-black/20 font-sans text-base" />
      <small id="name-hint" class="text-muted-foreground">Sets the filename when sharing.</small>
    </label>
    {#if pageCount > 0}
      <p class="flex justify-between text-sm">
        <strong>Pages</strong>
        <span>{pageCount}</span>
      </p>
    {/if}
    <p class="flex justify-between text-sm">
      <strong>Size</strong>
      <span>{formatBytes(output.blob.size)}</span>
    </p>
  </div>

  <div class="flex gap-3">
    <Button variant="outline" class="h-14 flex-1 rounded-2xl border-border/70 bg-white/5 font-black text-foreground hover:bg-white/10" onclick={onDone}>Done</Button>
    <Button class="h-14 flex-1 rounded-2xl bg-primary font-black text-primary-foreground hover:bg-primary" onclick={share}>Share</Button>
  </div>
</section>
